import { wixHttpPost } from './wix-http';
import { normalizeSendCodeResponse, type SendCodeResult } from './phone-verification-result';
import { parseRetryAfter } from './resend-cooldown';

/**
 * Ask for a verification code, from the BROWSER rather than through our own pod.
 *
 * This is the whole point of the module. The Wix edge rate-limits the caller's IP, and asking through
 * our own API route made every web user in the world one caller — so the budget was shared by strangers
 * and exhausted daily, blocking people who had done nothing but type their number. Measured over eight
 * days: 135 people refused outright, all of it on four days. Calling it from the browser puts each user
 * on their own address, which is the fix.
 *
 * Reachable same-origin because this ONE function is mapped under our host (see the
 * `symphony.<domain>/phone-verification` mapping on `com.wixpress.nownia.ai-scheduling-assistant-server`).
 * Deliberately the narrow mapping rather than one that exposes the whole artifact: the other nineteen web
 * functions on it have no business being reachable here, and this way a second front door cannot appear by
 * accident.
 *
 * There is no fallback to a server-side route, and that is deliberate rather than an omission. One
 * existed while the mapping propagated; keeping it would have made a routing regression SILENT — traffic
 * would quietly return to the shared budget and start refusing people again, which is exactly the failure
 * this module was written to end. A send that cannot route now fails loudly instead, and shows up as a
 * 404 on `/phone-verification` in the web app's own access log.
 */
const SEND_URL = '/phone-verification';
const TOO_MANY_REQUESTS = 429;

// The code step's resend can also ask for a voice call, so the sender carries every option the
// endpoint accepts rather than the two the first screen happens to offer.
export type DeliveryOption = 'SMS' | 'WHATSAPP' | 'VOICE_CALL';

export interface SendCodeOutcome {
  result?: SendCodeResult;
  /** True when the send was REFUSED rather than failed — 429, on either of the two walls below. */
  refused: boolean;
  /**
   * On a refusal, how many seconds the server said to wait, from its `Retry-After`.
   *
   * Present means this is the TIMED wall — the endpoint throttles a caller for ~25s after each code —
   * and the refusal clears by itself. Absent means the header said nothing, which covers both the timed
   * wall answering without one and the edge's per-caller budget being spent for the day. Those two call
   * for opposite copy, so a caller that cannot tell them apart treats the first bare 429 as a wait and
   * only a second consecutive one as the budget (see `use-code-verification`).
   */
  retryAfterSeconds?: number;
}

/** The response off a thrown http-client error, whichever shape it arrived in. */
function responseOf(err: unknown): { status?: unknown; headers?: unknown } | undefined {
  const candidate = err as { response?: { status?: unknown; headers?: unknown } } | null;
  return candidate?.response ?? undefined;
}

/**
 * `Retry-After` off a thrown error, across the two header shapes a client can hand back: a plain object
 * (axios, which lower-cases its keys) and a `Headers` instance (fetch). Neither is guaranteed here, and
 * a missing header is an ordinary outcome rather than a fault — the caller has a fallback.
 */
function retryAfterHeaderOf(err: unknown): string | undefined {
  const headers = responseOf(err)?.headers;
  if (!headers) return undefined;
  if (typeof (headers as Headers).get === 'function') {
    return (headers as Headers).get('retry-after') ?? undefined;
  }
  const bag = headers as Record<string, unknown>;
  const raw = bag['retry-after'] ?? bag['Retry-After'];
  return typeof raw === 'string' ? raw : typeof raw === 'number' ? String(raw) : undefined;
}

/** The status off a thrown http-client error, whichever shape it arrived in. */
function httpStatusOf(err: unknown): number | undefined {
  const candidate = err as { status?: unknown } | null;
  const status = responseOf(err)?.status ?? candidate?.status;
  if (typeof status === 'number') return status;
  const match = /\b(4\d\d|5\d\d)\b/.exec(err instanceof Error ? err.message : String(err));
  return match ? Number(match[1]) : undefined;
}

/**
 * Logged on every send. The number is NEVER included — only the outcome.
 *
 * The aggregatable record of these outcomes is the BI event in `use-send-code-bi.ts`; this is for
 * reading one browser's console next to one user's report.
 */
const LOG = '[send-code]';

export async function sendVerificationCode(phone: string, deliveryOption: DeliveryOption): Promise<SendCodeOutcome> {
  const body = { verificationOption: { phoneOption: { phone, deliveryOption } } };
  try {
    const raw = await wixHttpPost<unknown>(SEND_URL, body);
    const result = normalizeSendCodeResponse(raw);
    console.info(`${LOG} ${deliveryOption} sent=${result.success} hasVerificationId=${!!result.verificationId}`);
    return { result, refused: false };
  } catch (err) {
    const status = httpStatusOf(err);
    if (status === TOO_MANY_REQUESTS) {
      const retryAfterSeconds = parseRetryAfter(retryAfterHeaderOf(err), Date.now());
      console.warn(`${LOG} ${deliveryOption} REFUSED (429) retryAfter=${retryAfterSeconds ?? 'none'}`);
      return { refused: true, ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }) };
    }
    console.warn(`${LOG} ${deliveryOption} failed status=${status ?? 'unknown'}`);
    throw err;
  }
}
